(() => {
  "use strict";

  const PUBLIC_SCHEMA = "lollenstein-chargenpass-public/v1";
  const DATE_FORMAT = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  const MONTH_YEAR_FORMAT = new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric" });
  const NUMBER_FORMAT = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 3 });
  const LABELS = Object.freeze({
    conservation: "Konservierung",
    drying: "Trocknung",
    field_days: "Tage bis zur Bergung",
    dust_reduction: "Entstaubung",
    stored_since: "Eingelagert seit",
  });
  const LEXICON_ALIASES = Object.freeze({
    me_pferd_mj_kg_ts: "verdauliche_energie_mj_kg",
    nfc: "nfc_nicht_faser_kohlenhydrate",
    schwefel_s: "schwefel_s_mg_kg",
    selen_se_mg_kg: "selen_se",
    jod_i_mg_kg: "jod_i",
    schimmelpilze_kbe_g: "schimmelpilze",
    hefen_kbe_g: "hefen",
    mucoraceen_kbe_g: "mucoraceen",
    bakterien_kbe_g: "bakterien",
    entstaubungsverfahren: "maschinell_entstaubt",
    masse_ca: "masse",
  });

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  }

  function appendText(parent, tag, className, text) {
    if (text === null || text === undefined || text === "") return null;
    const node = element(tag, className, text);
    parent.append(node);
    return node;
  }

  function parseLexicon(root) {
    const entries = new Map();
    for (const source of root.querySelectorAll("[data-ls-cp-lexicon-entry]")) {
      const text = (selector) => source.querySelector(selector)?.textContent?.trim() || "";
      const anchor = text("[data-ls-cp-lexicon-anchor]");
      if (!anchor || entries.has(anchor)) continue;
      entries.set(anchor, {
        anchor,
        title: text("[data-ls-cp-lexicon-title]"),
        short: text("[data-ls-cp-lexicon-short]"),
        long: text("[data-ls-cp-lexicon-long]"),
      });
    }
    return entries;
  }

  function metricContextText(metric) {
    if (!metric) return "";
    const details = [
      metric.basis ? `Bezugsbasis: ${metric.basis}` : null,
      metric.method ? `Analysemethode: ${metric.method}` : null,
      metric.uncertainty ? `Messunsicherheit: ${metric.uncertainty}` : null,
      metric.derived ? "Rechnerisch aus ausgewiesenen Einzelwerten abgeleitet" : null,
    ].filter(Boolean);
    return details.length ? `Messdetails dieser Charge: ${details.join(" · ")}.` : "";
  }

  function lexiconTerm(label, anchor, lexicon, metric = null) {
    const wrap = element("span", "pp-term-label");
    appendText(wrap, "span", "", label);
    const lookup = LEXICON_ALIASES[anchor] || anchor;
    const entry = lexicon?.get(lookup);
    if (!entry) return wrap;

    const button = element("button", "pp-term-help", "?");
    button.type = "button";
    button.setAttribute("aria-label", `Erklärung zu ${label} öffnen`);
    button.setAttribute("aria-haspopup", "dialog");
    button.setAttribute("data-pp-info-trigger", "");
    wrap.append(button);

    const source = element("span", "pp-term-help__source");
    source.hidden = true;
    source.setAttribute("data-pp-info-source", "");
    const addSourceText = (attribute, value) => {
      const node = appendText(source, "span", "", value);
      if (node) node.setAttribute(attribute, "");
    };
    addSourceText("data-pp-info-title", entry.title || label);
    addSourceText("data-pp-info-short", entry.short);
    addSourceText("data-pp-info-long", [entry.long, metricContextText(metric)].filter(Boolean).join(" "));
    addSourceText("data-pp-info-anchor", entry.anchor);
    wrap.append(source);
    return wrap;
  }

  function formatDate(value) {
    if (!value) return null;
    const date = new Date(value.length === 10 ? `${value}T12:00:00Z` : value);
    return Number.isNaN(date.getTime()) ? value : DATE_FORMAT.format(date);
  }

  function formatNumber(value) {
    if (value === null || value === undefined || value === "") return null;
    const number = Number(value);
    return Number.isFinite(number) ? NUMBER_FORMAT.format(number) : null;
  }

  function displayOperator(operator) {
    if (operator === "nicht_bestimmbar") return "nicht bestimmbar";
    return operator && operator !== "=" ? operator : "";
  }

  function resultText(metric) {
    const value = formatNumber(metric?.value);
    if (value === null) return "–";
    const unit = ["Verhältnis", "ohne Einheit"].includes(metric.unit) ? null : metric.unit;
    return [displayOperator(metric.operator), value, unit].filter(Boolean).join(" ");
  }

  function safeUrl(value) {
    try {
      const text = String(value || "");
      const isStandalonePreview = document.documentElement.hasAttribute("data-ls-standalone-preview");
      const isEmbeddedPreviewImage = [
        "data:image/jpeg;base64,",
        "data:image/png;base64,",
        "data:image/webp;base64,",
      ].some((prefix) => text.startsWith(prefix));
      if (isStandalonePreview && isEmbeddedPreviewImage) return text;
      const base = window.location.protocol === "file:" ? window.location.href : window.location.origin;
      const url = new URL(text, base);
      if (url.protocol === "file:" && window.location.protocol === "file:") return url.href;
      const isShopifyCdn = url.protocol === "https:" && (
        url.hostname === "cdn.shopify.com" || url.hostname.endsWith(".shopifycdn.com")
      );
      if (url.origin !== window.location.origin && !isShopifyCdn) return null;
      return url.href;
    } catch {
      return null;
    }
  }

  function safePdfFilename(value) {
    const filename = String(value || "")
      .split(/[\\/]/)
      .at(-1)
      .replace(/[<>:"|?*\u0000-\u001f]/g, "-")
      .trim();
    return filename && /\.pdf$/i.test(filename) ? filename : "laborbericht.pdf";
  }

  function hasValues(object) {
    return object && Object.values(object).some((value) => value !== null && value !== undefined && value !== "");
  }

  function hydrateLiquidAssets(payload, assets) {
    const hydrated = JSON.parse(JSON.stringify(payload));
    const productImages = Array.isArray(assets?.product_images) ? assets.product_images : [];
    const imageMetadata = Array.isArray(hydrated?.charge?.product_images)
      ? hydrated.charge.product_images
      : [];

    if (hydrated?.charge) {
      hydrated.charge.product_images = productImages.map((asset, index) => ({
        ...(imageMetadata[index] || {}),
        url: asset?.url,
        width: Number(asset?.width) || null,
        height: Number(asset?.height) || null,
        alt_text: imageMetadata[index]?.alt_text || asset?.alt || null,
        source: "liquid_file_reference",
      }));

      const laboratoryReport = assets?.labor_report?.url
        ? { url: assets.labor_report.url, source: "liquid_file_reference" }
        : null;
      for (const collectionKey of ["analyses", "hygiene_analyses"]) {
        const analyses = Array.isArray(hydrated.charge[collectionKey])
          ? hydrated.charge[collectionKey]
          : [];
        for (const analysis of analyses) {
          analysis.report = analysis.report && laboratoryReport
            ? { ...analysis.report, ...laboratoryReport }
            : null;
        }
      }

      // Version 1 has no dedicated file_reference field for chronology media.
      // Never fall back to URLs carried in the public JSON payload.
      const chronology = Array.isArray(hydrated.charge.chronology)
        ? hydrated.charge.chronology
        : [];
      for (const entry of chronology) entry.media = [];
    }

    return hydrated;
  }

  function section(title, className = "") {
    const node = element("section", `ls-cp__section ${className}`.trim());
    appendText(node, "h3", "ls-cp__section-title", title);
    return node;
  }

  function disclosureSection(title, { className = "", open = false } = {}) {
    const node = element("details", `ls-cp__disclosure ${className}`.trim());
    node.open = open;
    const summary = element("summary", "ls-cp__disclosure-summary");
    appendText(summary, "span", "ls-cp__disclosure-title", title);
    node.append(summary);
    const body = element("div", "ls-cp__disclosure-body");
    node.append(body);
    return { node, body };
  }

  function definitionList(entries, className = "") {
    const list = element("dl", `ls-cp__facts ${className}`.trim());
    for (const [label, value] of entries) {
      if (value === null || value === undefined || value === "") continue;
      const group = element("div", "ls-cp__fact");
      appendText(group, "dt", "ls-cp__fact-label", label);
      appendText(group, "dd", "ls-cp__fact-value", value);
      list.append(group);
    }
    return list;
  }

  function renderNotice(content, title, message, tone = "neutral") {
    const notice = element("div", `ls-cp__notice ls-cp__notice--${tone}`);
    appendText(notice, "h3", "ls-cp__notice-title", title);
    appendText(notice, "p", "ls-cp__notice-text", message);
    content.append(notice);
  }

  function renderIdentity(content, data) {
    const charge = data.charge || {};
    const quality = data.quality || {};
    const wrap = element("div", "ls-cp__identity");
    const main = element("div", "ls-cp__identity-main");
    appendText(main, "p", "ls-cp__kicker", charge.feed_type || "Raufutter");
    appendText(main, "p", "ls-cp__product-title", data.variant?.product_title);
    appendText(main, "h3", "ls-cp__charge-number", charge.number || "Charge");
    const meta = [
      charge.cut,
      charge.harvest_year ? `Ernte ${charge.harvest_year}` : null,
      charge.pressed_on ? `Gepresst am ${formatDate(charge.pressed_on)}` : null,
    ].filter(Boolean);
    appendText(main, "p", "ls-cp__identity-meta", meta.join(" · "));
    wrap.append(main);

    content.append(wrap);
  }

  function renderDescription(content, description, state) {
    if (!description?.summary && !description?.body) return;
    const isGeneratedDraft = description.generation?.status === "draft_review_required";
    if (isGeneratedDraft && state !== "review") return;
    const node = section(null, "ls-cp__section--wide");
    if (isGeneratedDraft) {
      const status = element("div", "ls-cp__draft-status");
      appendText(status, "strong", "", "Noch nicht veröffentlicht");
      appendText(
        status,
        "span",
        "",
        "Dieser Text wurde aus belegten Chargendaten erzeugt und muss vor einer Veröffentlichung fachlich und redaktionell geprüft werden.",
      );
      node.append(status);
    }
    appendText(node, "p", "ls-cp__lead", description.summary);
    const highlights = Array.isArray(description.highlights) ? description.highlights.slice(0, 4) : [];
    if (highlights.length) {
      const list = element("ul", "ls-cp__selling-points");
      for (const highlight of highlights) appendText(list, "li", "ls-cp__selling-point", highlight);
      node.append(list);
    }
    if (description.body) {
      for (const paragraph of description.body.split(/(?:\n{2,}|\\n\\n)/).filter(Boolean)) {
        appendText(node, "p", "ls-cp__body", paragraph);
      }
    }
    content.append(node);
  }

  function renderClassification(content, classification, state) {
    if (!classification || !Array.isArray(classification.profile) || !classification.profile.length) return;
    if (state !== "review" && classification.release?.storefront_allowed !== true) return;
    const node = section("Automatisch ermitteltes Futterprofil", "ls-cp__section--wide ls-cp__section--classification");
    const status = element("div", "ls-cp__classification-status");
    appendText(status, "strong", "", classification.label || "Automatischer Entwurf");
    appendText(status, "span", "", classification.release?.reason);
    node.append(status);

    const profile = element("div", "ls-cp__classification-grid");
    for (const item of classification.profile) {
      const card = element("article", "ls-cp__classification-card");
      appendText(card, "span", "ls-cp__trait-label", item.label);
      appendText(card, "strong", "ls-cp__classification-value", item.value);
      appendText(card, "p", "ls-cp__muted", item.explanation);
      profile.append(card);
    }
    node.append(profile);

    const labFacts = Array.isArray(classification.lab_facts) ? classification.lab_facts : [];
    if (labFacts.length) {
      appendText(node, "h4", "ls-cp__subheading", "Belegte Laborwerte im Textentwurf");
      const facts = element("div", "ls-cp__classification-facts");
      for (const fact of labFacts) {
        const card = element("div", "ls-cp__classification-fact");
        appendText(card, "span", "ls-cp__trait-label", fact.label);
        appendText(card, "strong", "ls-cp__classification-value", fact.result);
        appendText(card, "span", "ls-cp__muted", fact.basis);
        facts.append(card);
      }
      node.append(facts);
    }

    const blocked = Array.isArray(classification.blocked) ? classification.blocked : [];
    if (blocked.length) {
      const details = element("details", "ls-cp__classification-blocked");
      appendText(details, "summary", "ls-cp__metric-summary", "Was die Automatik bewusst noch nicht bewertet");
      const list = element("ul", "ls-cp__classification-blocked-list");
      for (const item of blocked) {
        const entry = element("li");
        appendText(entry, "strong", "", item.label);
        appendText(entry, "span", "", item.reason ? `: ${item.reason}` : null);
        list.append(entry);
      }
      details.append(list);
      node.append(details);
    }

    node.append(definitionList([
      ["Regelwerk", classification.ruleset?.id],
      ["Regelversion", classification.ruleset?.version],
      ["Verwendete Laborprobe", Array.isArray(classification.source_analysis_samples) ? classification.source_analysis_samples.join(", ") : null],
    ], "ls-cp__facts--compact"));
    content.append(node);
  }

  function renderProductImages(content, images) {
    if (!Array.isArray(images) || !images.length) return;
    const node = section("Produktbilder dieser Charge", "ls-cp__section--wide ls-cp__section--gallery");
    appendText(
      node,
      "p",
      "ls-cp__body",
      "Diese Aufnahmen zeigen das Futter aus genau dieser dokumentierten Charge.",
    );
    const gallery = element("div", "ls-cp__product-gallery");
    for (const item of images) {
      if (item?.source !== "liquid_file_reference") continue;
      const url = safeUrl(item.url);
      if (!url) continue;
      const figure = element("figure", "ls-cp__product-photo");
      const image = element("img", "ls-cp__product-photo-image");
      image.src = url;
      image.alt = item.alt_text || item.title || "Produktaufnahme der dokumentierten Charge";
      image.loading = "lazy";
      image.decoding = "async";
      if (Number.isSafeInteger(item.width) && item.width > 0) image.width = item.width;
      if (Number.isSafeInteger(item.height) && item.height > 0) image.height = item.height;
      figure.append(image);
      if (item.title || item.description || item.captured_at) {
        const caption = element("figcaption", "ls-cp__product-photo-caption");
        appendText(caption, "strong", "", item.title);
        appendText(caption, "span", "", item.description);
        if (item.captured_at) appendText(caption, "time", "", `Aufgenommen am ${formatDate(item.captured_at)}`);
        figure.append(caption);
      }
      gallery.append(figure);
    }
    if (!gallery.childElementCount) return;
    node.append(gallery);
    content.append(node);
  }

  function renderMixture(content, mixture, area, lexicon) {
    if (!mixture && !hasValues(area)) return;
    const mixtureTitle = [mixture?.type, mixture?.name].filter(Boolean).join(" ").trim();
    const disclosure = disclosureSection(mixtureTitle || "Pflanzenbestand");
    disclosure.node.classList.add("ls-cp__disclosure--mixture");
    const node = disclosure.body;

    const components = Array.isArray(mixture?.components) ? mixture.components : [];
    const sowingYear = area?.sowing_year;
    const standOrigin = area?.stand_origin && sowingYear
      ? `${area.stand_origin} aus ${String(sowingYear).trim()}`
      : area?.stand_origin;
    const areaValue = [area?.type, standOrigin].filter(Boolean).join(" · ");
    const origin = String(area?.origin || "").trim();
    const country = String(area?.origin_country || "").trim();
    const originValue = origin && country && !origin.toLocaleLowerCase("de").includes(country.toLocaleLowerCase("de"))
      ? `${origin}, ${country}`
      : origin || country;
    const facts = [
      ["Flächentyp", areaValue, "flaechentyp"],
      ["Herkunft", originValue, "erntegebiet"],
    ].filter(([, value]) => value !== null && value !== undefined && value !== "");

    if (facts.length) {
      const factGrid = element("dl", "ls-cp__plant-facts");
      for (const [label, value, anchor] of facts) {
        const fact = element("div", "ls-cp__plant-fact");
        const term = element("dt", "ls-cp__plant-fact-label");
        term.append(lexiconTerm(label, anchor, lexicon));
        appendText(fact, "dd", "ls-cp__plant-fact-value", value);
        fact.prepend(term);
        factGrid.append(fact);
      }
      node.append(factGrid);
    }

    if (components.length) {
      const list = element("ul", "ls-cp__composition");
      for (const component of components) {
        const item = element("li", "ls-cp__composition-item");
        const name = element("div", "ls-cp__composition-name");
        const title = element("strong", "ls-cp__composition-title");
        const componentLexicon = new Map(lexicon || []);
        if (component.lexicon?.anchor) {
          componentLexicon.set(component.lexicon.anchor, {
            anchor: component.lexicon.anchor,
            title: component.lexicon.title || component.plant,
            short: component.lexicon.short || "",
            long: component.lexicon.long || "",
          });
        }
        title.append(
          lexiconTerm(
            component.plant || "Pflanzenart",
            component.lexicon?.anchor || "",
            componentLexicon,
          ),
        );
        name.append(title);
        if (component.botanical_name) appendText(name, "em", "", component.botanical_name);
        item.append(name);

        const share = component.share_percent !== null
          ? `${formatNumber(component.share_percent)} %`
          : "Anteil nicht beziffert";
        appendText(item, "span", "ls-cp__composition-share", share);
        appendText(
          item,
          "p",
          "ls-cp__composition-description",
          component.description || component.lexicon?.short,
        );
        list.append(item);
      }
      node.append(list);
      appendText(
        node,
        "p",
        "ls-cp__footnote",
        "Die Anteile beschreiben die dokumentierte Saatgutrezeptur. Der tatsächliche Aufwuchs kann sich durch Standort, Witterung und Nutzung verändern.",
      );
    }
    content.append(disclosure.node);
  }

  function renderProcess(content, process) {
    if (!hasValues(process)) return;
    const disclosure = disclosureSection("Ernte & Verarbeitung");
    const node = disclosure.body;
    const entries = Object.entries(LABELS).map(([key, label]) => {
      let value = process[key];
      if (key === "field_days" && value !== null && value !== undefined) value = `${formatNumber(value)} Tage`;
      if (key === "stored_since") value = formatDate(value);
      return [label, value];
    });
    node.append(definitionList(entries));
    content.append(disclosure.node);
  }

  function characteristicTable(items, caption, lexicon, rowOffset = 0) {
    const wrap = element("div", "ls-cp__table-wrap");
    const table = element("table", "ls-cp__table");
    appendText(table, "caption", "visually-hidden", caption);
    const head = element("thead");
    const headingRow = element("tr");
    for (const label of ["Merkmal", "Ausprägung"]) appendText(headingRow, "th", "", label);
    head.append(headingRow);
    table.append(head);

    const body = element("tbody");
    for (const [index, trait] of items.entries()) {
      const rowNumber = rowOffset + index;
      const row = element(
        "tr",
        rowNumber % 2 === 0 ? "ls-cp__metric-row--odd" : "ls-cp__metric-row--even",
      );
      const nameCell = element("th");
      nameCell.scope = "row";
      const name = element("span", "ls-cp__metric-name");
      name.append(lexiconTerm(trait.label, trait.code, lexicon));
      nameCell.append(name);
      row.append(nameCell);
      let value = trait.value;
      if (trait.format === "month_year" && value) {
        const date = new Date(`${value}T12:00:00Z`);
        if (!Number.isNaN(date.getTime())) value = MONTH_YEAR_FORMAT.format(date);
      }
      appendText(row, "td", "ls-cp__result", value);
      body.append(row);
    }

    table.append(body);
    wrap.append(table);
    return wrap;
  }

  function characteristicTableColumns(items, lexicon) {
    const source = Array.isArray(items) ? items.filter(Boolean) : [];
    if (source.length < 2) return characteristicTable(source, "Futtermerkmale", lexicon);

    const splitAt = Math.ceil(source.length / 2);
    const columns = element("div", "ls-cp__metric-tables");
    columns.append(characteristicTable(source.slice(0, splitAt), "Futtermerkmale, Teil 1", lexicon));
    columns.append(characteristicTable(source.slice(splitAt), "Futtermerkmale, Teil 2", lexicon, splitAt));
    return columns;
  }

  function renderCharacteristics(content, characteristics, lexicon) {
    const items = Array.isArray(characteristics)
      ? characteristics.filter(Boolean)
      : (Array.isArray(characteristics?.items) ? characteristics.items.filter(Boolean) : []);
    const details = Array.isArray(characteristics?.details)
      ? characteristics.details.filter((item) => item && item.value !== null && item.value !== undefined && item.value !== "")
      : [];
    if (!items.length && !details.length) return;

    const disclosure = disclosureSection("Futtermerkmale & Produktangaben");
    const groups = element("div", "ls-cp__characteristic-groups");

    if (items.length) {
      const group = element("section", "ls-cp__characteristic-group");
      appendText(group, "h3", "ls-cp__characteristic-group-title", "Futtermerkmale");
      group.append(characteristicTable(items, "Futtermerkmale", lexicon));
      groups.append(group);
    }

    if (details.length) {
      const group = element("section", "ls-cp__characteristic-group");
      appendText(group, "h3", "ls-cp__characteristic-group-title", "Produktangaben");
      group.append(characteristicTable(details, "Produktangaben", lexicon));
      groups.append(group);
    }

    disclosure.body.append(groups);
    appendText(disclosure.body, "p", "ls-cp__footnote", characteristics?.note);
    content.append(disclosure.node);
  }

  function targetRangeText(target, metric) {
    const min = formatNumber(target.min);
    const max = formatNumber(target.max);
    const unit = ["Verhältnis", "ohne Einheit"].includes(metric.unit) ? "" : ` ${metric.unit || ""}`;
    if (min !== null && max !== null) {
      const left = target.min_inclusive ? "≥" : ">";
      const right = target.max_inclusive ? "≤" : "<";
      return `${left} ${min} bis ${right} ${max}${unit}`.trim();
    }
    if (min !== null) return `${target.min_inclusive ? "≥" : ">"} ${min}${unit}`.trim();
    if (max !== null) return `${target.max_inclusive ? "≤" : "<"} ${max}${unit}`.trim();
    return "Kein Zahlenbereich hinterlegt";
  }

  function relationText(relation, targetTone) {
    if (relation === "nicht_eindeutig") {
      return "Mit Operator oder Bezugsbasis nicht eindeutig vergleichbar";
    }
    if (relation === "innerhalb") {
      if (targetTone === "positive") return "Der Messwert liegt im günstigen Zielbereich – für dieses Fütterungsziel ist das positiv.";
      if (targetTone === "warning") return "Der Messwert liegt im bedingt geeigneten Bereich. Ob die Charge passt, hängt von Pferdeprofil, Futtermenge und Gesamtration ab.";
      if (targetTone === "critical") return "Der Messwert liegt im nicht empfohlenen Bereich – für dieses Fütterungsziel ist das ungünstig.";
      return "Der Messwert liegt in diesem Orientierungsbereich.";
    }
    const direction = relation === "unterhalb" ? "unterhalb" : "oberhalb";
    if (targetTone === "positive") {
      return `Der Messwert liegt ${direction} des günstigen Zielbereichs und damit außerhalb dieses Zielwerts. Ob das nachteilig ist, ergibt sich aus der freigegebenen Gesamtbewertung.`;
    }
    if (targetTone === "warning") {
      return `Der Messwert liegt ${direction} des bedingt geeigneten Bereichs. Entscheidend bleibt die freigegebene Gesamtbewertung.`;
    }
    if (targetTone === "critical") {
      return `Der Messwert liegt ${direction} des nicht empfohlenen Bereichs. Außerhalb dieses Bereichs ist der Einzelwert nicht automatisch gut; entscheidend bleibt die Gesamtbewertung.`;
    }
    return `Der Messwert liegt ${direction} dieses Bereichs. Die Bedeutung ergibt sich aus der freigegebenen Gesamtbewertung.`;
  }

  function renderMetricGuidance(parent, metric) {
    const guidance = Array.isArray(metric.guidance) ? metric.guidance : [];
    if (!guidance.length) return;
    const wrap = element("div", "ls-cp__metric-guidance");
    for (const item of guidance) {
      const card = element("article", "ls-cp__metric-guidance-card");
      appendText(card, "p", "ls-cp__kicker", item.area);
      appendText(card, "h5", "ls-cp__guidance-title", item.category);
      card.append(definitionList([
        ["Gesamtbewertung der Charge", item.evaluation],
        ["Regelversion", item.rule_version],
        ["Evidenzstufe", item.evidence],
      ], "ls-cp__facts--compact"));
      const targets = element("div", "ls-cp__target-list");
      for (const target of Array.isArray(item.targets) ? item.targets : []) {
        const targetNode = element("div", `ls-cp__target ls-cp__target--${target.tone || "neutral"}`);
        const heading = element("div", "ls-cp__target-heading");
        appendText(heading, "strong", "ls-cp__target-level", target.level);
        appendText(heading, "span", "ls-cp__target-range", targetRangeText(target, metric));
        targetNode.append(heading);
        appendText(targetNode, "p", "ls-cp__target-relation", relationText(target.relation, target.tone));
        targets.append(targetNode);
      }
      if (targets.childElementCount) card.append(targets);
      appendText(card, "p", "ls-cp__body", item.rule_note);
      appendText(card, "p", "ls-cp__source-note", item.source ? `Fachliche Grundlage: ${item.source}` : null);
      appendText(card, "p", "ls-cp__footnote", item.safety_note);
      wrap.append(card);
    }
    appendText(
      wrap,
      "p",
      "ls-cp__footnote",
      "Unter- oder Überschreitungen sind nicht pauschal gut oder schlecht: Die Bedeutung hängt vom Fütterungsziel und von weiteren Messwerten ab. Maßgeblich ist deshalb die freigegebene Gesamtbewertung der Charge.",
    );
    parent.append(wrap);
  }

  function metricGuidanceDetails(metric) {
    const hasGuidance = Array.isArray(metric.guidance) && metric.guidance.length > 0;
    if (!hasGuidance) return null;
    const details = element("details", "ls-cp__metric-guidance-details");
    const summary = element("summary", "ls-cp__metric-guidance-summary", "Chargenbezogene Einordnung");
    details.append(summary);
    renderMetricGuidance(details, metric);
    return details;
  }

  function metricTable(metrics, caption, lexicon, rowOffset = 0) {
    const wrap = element("div", "ls-cp__table-wrap");
    const table = element("table", "ls-cp__table");
    appendText(table, "caption", "visually-hidden", caption);
    const head = element("thead");
    const row = element("tr");
    for (const label of ["Parameter", "Wert"]) appendText(row, "th", "", label);
    head.append(row);
    table.append(head);
    const body = element("tbody");
    for (const [index, metric] of metrics.entries()) {
      const rowNumber = rowOffset + index;
      const metricRow = element(
        "tr",
        rowNumber % 2 === 0 ? "ls-cp__metric-row--odd" : "ls-cp__metric-row--even",
      );
      const nameCell = element("th");
      nameCell.scope = "row";
      const name = element("span", "ls-cp__metric-name");
      name.append(lexiconTerm(metric.label || metric.code, metric.code, lexicon, metric));
      nameCell.append(name);
      const details = metricGuidanceDetails(metric);
      if (details) nameCell.append(details);
      metricRow.append(nameCell);
      appendText(metricRow, "td", "ls-cp__result", resultText(metric));
      body.append(metricRow);
    }
    table.append(body);
    wrap.append(table);
    return wrap;
  }

  function metricTableColumns(metrics, caption, lexicon) {
    const source = Array.isArray(metrics) ? metrics.filter(Boolean) : [];
    if (source.length < 2) return metricTable(source, caption, lexicon);

    const splitAt = Math.ceil(source.length / 2);
    const columns = element("div", "ls-cp__metric-tables");
    columns.append(metricTable(source.slice(0, splitAt), `${caption}, Teil 1`, lexicon));
    columns.append(metricTable(source.slice(splitAt), `${caption}, Teil 2`, lexicon, splitAt));
    return columns;
  }

  function reportLink(report) {
    if (report?.source !== "liquid_file_reference") return null;
    const url = safeUrl(report?.url);
    const mimeType = String(report?.mime_type || "application/pdf").split(";", 1)[0].trim().toLowerCase();
    if (!report || !url || mimeType !== "application/pdf") return null;
    const label = report.title
      ? `${report.title} als PDF öffnen / herunterladen`
      : "Laborbericht als PDF öffnen / herunterladen";
    const link = element("a", "ls-cp__download", label);
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.download = safePdfFilename(report.filename);
    link.setAttribute("type", "application/pdf");
    if (report.description) link.setAttribute("aria-label", `${label}. ${report.description}`);
    return link;
  }

  function analysisMeta(analysis) {
    return definitionList([
      ["Proben-ID", analysis.sample_id],
      ["Probenahme", formatDate(analysis.sample_taken_on)],
      ["Analyse / Bericht", formatDate(analysis.analysed_on)],
      ["Untersuchendes Labor", analysis.laboratory?.name],
    ], "ls-cp__facts--compact");
  }

  function renderLaboratoryDisclosure(content, analyses, lexicon, options) {
    const source = Array.isArray(analyses)
      ? analyses.filter((analysis) => Array.isArray(analysis?.[options.valuesKey]) && analysis[options.valuesKey].length)
      : [];
    if (!source.length && options.hideWhenEmpty) return;

    const disclosure = disclosureSection(options.title);
    const node = disclosure.body;
    if (!source.length) {
      appendText(node, "p", "ls-cp__empty", options.emptyMessage);
      content.append(disclosure.node);
      return;
    }

    for (const analysis of source) {
      const card = element("article", "ls-cp__analysis");
      const values = analysis[options.valuesKey];
      card.append(metricTableColumns(values, `${analysis.title || options.title}: ${options.caption}`, lexicon));
      if (options.assessmentKey) appendText(card, "p", "ls-cp__body", analysis[options.assessmentKey]);
      const meta = element("div", "ls-cp__hygiene-meta");
      appendText(meta, "h4", "ls-cp__analysis-title", analysis.title);
      meta.append(analysisMeta(analysis));
      if (analysis.laboratory?.description) appendText(meta, "p", "ls-cp__muted", analysis.laboratory.description);
      card.append(meta);
      const link = reportLink(analysis.report);
      if (link) card.append(link);
      node.append(card);
    }

    content.append(disclosure.node);
  }

  function renderAnalyses(content, analyses, lexicon) {
    renderLaboratoryDisclosure(content, analyses, lexicon, {
      title: "Nährwerte & Inhaltsstoffe",
      valuesKey: "metrics",
      caption: "Nährwerte und Inhaltsstoffe",
      emptyMessage: "Für diese Charge sind noch keine Nährwerte oder Inhaltsstoffe zur öffentlichen Anzeige hinterlegt.",
      hideWhenEmpty: true,
    });
    renderLaboratoryDisclosure(content, analyses, lexicon, {
      title: "Mineralstoffe & Spurenelemente",
      valuesKey: "minerals",
      caption: "Mineralstoffe und Spurenelemente",
      emptyMessage: "Für diese Charge sind noch keine Mineralstoffe oder Spurenelemente zur öffentlichen Anzeige hinterlegt.",
      hideWhenEmpty: true,
    });
  }

  function renderHygiene(content, analyses, lexicon) {
    renderLaboratoryDisclosure(content, analyses, lexicon, {
      title: "Futterhygiene",
      valuesKey: "metrics",
      caption: "Hygieneparameter",
      assessmentKey: "laboratory_assessment",
      hideWhenEmpty: true,
    });
  }

  function renderQualityPromise(content, qualityPromise) {
    if (!qualityPromise) return;
    const paragraphs = Array.isArray(qualityPromise.paragraphs)
      ? qualityPromise.paragraphs
      : [];
    if (!qualityPromise.introduction && !qualityPromise.title && !paragraphs.length) return;

    const disclosure = disclosureSection("Unser Qualitätsversprechen");
    const card = element("div", "ls-cp__quality-promise");
    appendText(card, "p", "ls-cp__quality-promise-intro", qualityPromise.introduction);
    appendText(card, "h3", "ls-cp__quality-promise-title", qualityPromise.title);

    for (const paragraph of paragraphs) {
      const segments = Array.isArray(paragraph?.segments) ? paragraph.segments : [];
      if (!segments.length) continue;
      const node = element("p", "ls-cp__quality-promise-text");
      for (const segment of segments) {
        if (!segment?.text) continue;
        node.append(element(segment.bold ? "strong" : "span", "", segment.text));
      }
      if (node.childNodes.length) card.append(node);
    }

    disclosure.body.append(card);
    content.append(disclosure.node);
  }

  function renderSuitability(content, suitability) {
    if (!Array.isArray(suitability) || !suitability.length) return;
    const valid = suitability.filter((item) => item?.category && item?.rating);
    if (!valid.length) return;

    const featured = valid.filter((item) => item.tone === "positive");
    const further = valid.filter((item) => item.tone !== "positive");
    const node = section("Für welches Pferd passt diese Charge?", "ls-cp__section--wide ls-cp__section--fit");
    appendText(
      node,
      "p",
      "ls-cp__fit-intro",
      "Die Einschätzung bezieht sich ausschließlich auf die freigegebenen Eigenschaften und Laborwerte dieser Charge.",
    );

    if (featured.length) {
      const cards = element("div", "ls-cp__fit-grid");
      for (const item of featured) {
        const card = element("article", "ls-cp__fit-card");
        const head = element("div", "ls-cp__fit-card-head");
        const icon = element("span", "ls-cp__fit-icon", "✓");
        icon.setAttribute("aria-hidden", "true");
        head.append(icon);
        const title = element("div", "ls-cp__fit-card-title");
        appendText(title, "span", "ls-cp__fit-area", item.area);
        appendText(title, "h4", "ls-cp__card-title", item.category);
        head.append(title);
        card.append(head);
        appendText(card, "strong", "ls-cp__fit-rating", item.rating);
        appendText(card, "p", "ls-cp__fit-reason", item.reason);
        appendText(card, "p", "ls-cp__fit-note", item.customer_note);
        cards.append(card);
      }
      node.append(cards);
    }

    if (further.length) {
      const details = element("details", "ls-cp__fit-more");
      const summary = element("summary", "ls-cp__fit-more-summary");
      appendText(summary, "span", "", "Weitere Einordnungen dieser Charge");
      appendText(summary, "span", "ls-cp__fit-count", further.length);
      details.append(summary);
      const list = element("div", "ls-cp__fit-list");
      for (const item of further) {
        const tone = ["warning", "critical"].includes(item.tone) ? item.tone : "neutral";
        const row = element("article", `ls-cp__fit-row ls-cp__fit-row--${tone}`);
        const rowHead = element("div", "ls-cp__fit-row-head");
        appendText(rowHead, "h4", "ls-cp__fit-row-title", item.category);
        appendText(rowHead, "strong", `ls-cp__fit-badge ls-cp__fit-badge--${tone}`, item.rating);
        row.append(rowHead);
        appendText(row, "p", "ls-cp__fit-reason", item.reason);
        appendText(row, "p", "ls-cp__fit-note", item.customer_note);
        list.append(row);
      }
      details.append(list);
      node.append(details);
    }

    appendText(
      node,
      "p",
      "ls-cp__fit-footnote",
      "Die Einordnung ersetzt keine individuelle Rationsberechnung. Futtermenge, Arbeitsleistung, Körperzustand und Vorerkrankungen müssen immer mit betrachtet werden.",
    );
    content.append(node);
  }

  function renderMedia(media) {
    if (!Array.isArray(media) || !media.length) return null;
    const gallery = element("div", "ls-cp__media");
    for (const item of media) {
      if (item?.source !== "liquid_file_reference") continue;
      const url = safeUrl(item.url);
      if (!url) continue;
      const figure = element("figure", "ls-cp__media-item");
      if (item.kind === "image") {
        const image = element("img", "ls-cp__media-image");
        image.src = url;
        image.alt = item.alt_text || item.title || "Dokumentierte Chargenaufnahme";
        image.loading = "lazy";
        image.decoding = "async";
        figure.append(image);
      } else {
        const link = element("a", "ls-cp__download", item.title || "Medium öffnen");
        link.href = url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        figure.append(link);
      }
      if (item.title || item.description) {
        const caption = element("figcaption", "ls-cp__media-caption");
        appendText(caption, "strong", "", item.title);
        appendText(caption, "span", "", item.description);
        figure.append(caption);
      }
      gallery.append(figure);
    }
    return gallery.childElementCount ? gallery : null;
  }

  function renderChronology(content, entries) {
    if (!Array.isArray(entries) || !entries.length) return;
    const node = section("Der Weg dieser Charge", "ls-cp__section--wide");
    const list = element("ol", "ls-cp__timeline");
    for (const entry of entries) {
      const item = element("li", "ls-cp__timeline-item");
      const marker = element("span", "ls-cp__timeline-marker");
      marker.setAttribute("aria-hidden", "true");
      item.append(marker);
      const body = element("div", "ls-cp__timeline-body");
      const dates = entry.date_status === "zeitraum" && entry.date_until
        ? `${formatDate(entry.date)} bis ${formatDate(entry.date_until)}`
        : `${entry.date_status === "ungefaehr" ? "ca. " : ""}${formatDate(entry.date) || "Datum nicht angegeben"}`;
      appendText(body, "time", "ls-cp__timeline-date", dates);
      appendText(body, "h4", "ls-cp__timeline-title", entry.title || entry.phase);
      appendText(body, "p", "ls-cp__body", entry.description);
      const media = renderMedia(entry.media);
      if (media) body.append(media);
      item.append(body);
      list.append(item);
    }
    node.append(list);
    content.append(node);
  }

  function renderMethodology(content, methodology) {
    if (!methodology) return;
    const details = element("details", "ls-cp__methodology");
    appendText(details, "summary", "ls-cp__methodology-summary", "So entstehen unsere Angaben");
    const body = element("div", "ls-cp__methodology-body");
    for (const value of Object.values(methodology)) appendText(body, "p", "ls-cp__body", value);
    details.append(body);
    content.append(details);
  }

  function parseVariantId(value) {
    const text = String(value || "").trim();
    return /^[1-9][0-9]{0,19}$/.test(text) ? text : null;
  }

  function variantFromUrl() {
    return parseVariantId(new URL(window.location.href).searchParams.get("variant"));
  }

  function variantFromEventTarget(target) {
    const scope = target?.closest?.('form[action*="/cart/add"], product-info, product-form, [data-product-form]');
    return parseVariantId(scope?.querySelector?.('[name="id"]')?.value);
  }

  function parseSnapshots(root) {
    const snapshots = new Map();
    for (const source of root.querySelectorAll("[data-ls-cp-snapshot]")) {
      try {
        const envelope = JSON.parse(source.textContent || "null");
        const rawPayload = envelope?.payload || envelope;
        const payload = hydrateLiquidAssets(rawPayload, envelope?.assets);
        if (!payload || payload.schema !== PUBLIC_SCHEMA || payload.state !== "ready") continue;
        const variantId = parseVariantId(source.dataset.variantId) || "single";
        if (envelope?.valid_until && new Date(envelope.valid_until).getTime() < Date.now()) {
          payload.quality = {
            code: "snapshot_abgelaufen",
            label: "Status wird aktualisiert",
            tone: "neutral",
            as_of: null,
          };
        }
        snapshots.set(variantId, payload);
      } catch {
        // Fail closed: malformed snapshot data is treated as unavailable.
      }
    }
    return snapshots;
  }

  function findLooxReviewsNode(root) {
    const widget = document.getElementById("looxReviews");
    const widgetBlock = widget?.closest(".shopify-app-block, .shopify-block") || widget;
    if (widgetBlock && !root?.contains(widgetBlock)) return widgetBlock;

    const fallback = document.querySelector(
      '[id*="__loox_reviews_loox_dynamic_section_"], [id*="loox_reviews_loox_dynamic_section"]',
    );
    if (fallback && !root?.contains(fallback)) return fallback;
    return null;
  }

  function renderCustomerReviews(content, reviewsNode) {
    const disclosure = disclosureSection("Kundenbewertungen");
    const slot = element("div", "ls-cp__reviews");
    slot.dataset.lsCpReviewsSlot = "";
    if (reviewsNode) slot.append(reviewsNode);
    disclosure.body.append(slot);
    content.append(disclosure.node);
  }

  class ChargenpassController {
    constructor(root) {
      this.root = root;
      this.status = root.querySelector("[data-ls-cp-status]");
      this.content = root.querySelector("[data-ls-cp-content]");
      this.mode = root.dataset.mode === "single" ? "single" : "product";
      this.hasCustomerReviews =
        this.mode === "product" && root.dataset.hasCustomerReviews === "true";
      this.snapshots = parseSnapshots(root);
      this.lexicon = parseLexicon(root);
      this.activeVariantId = null;
      this.refreshTimer = null;
      this.hideWithoutCharge = root.dataset.hideWithoutCharge === "true";
      this.designMode = root.dataset.designMode === "true";
      this.guaranteeHome = this.mode === "product"
        ? document.querySelector(`[data-ls-guarantee-home][data-product-id="${root.dataset.productId}"]`)
        : null;
      this.guaranteeNode = this.guaranteeHome?.querySelector("[data-ls-guarantee]");
      this.reviewsNode = null;
      this.reviewsObserver = null;
      this.reviewsObserverTimer = null;
      this.onPotentialVariantChange = this.onPotentialVariantChange.bind(this);
      this.onCustomVariantChange = this.onCustomVariantChange.bind(this);
    }

    stopReviewsObserver() {
      this.reviewsObserver?.disconnect();
      this.reviewsObserver = null;
      if (this.reviewsObserverTimer) window.clearTimeout(this.reviewsObserverTimer);
      this.reviewsObserverTimer = null;
    }

    mountReviews() {
      if (this.mode !== "product") return;

      const reviewsNode = this.reviewsNode || findLooxReviewsNode(this.root);
      if (!this.hasCustomerReviews) {
        if (reviewsNode) {
          reviewsNode.hidden = true;
          reviewsNode.setAttribute("aria-hidden", "true");
          reviewsNode.style.display = "none";
          this.stopReviewsObserver();
          return;
        }
      } else {
        const slot = this.root.querySelector("[data-ls-cp-reviews-slot]");
        if (!slot) return;
        if (reviewsNode) {
          this.reviewsNode = reviewsNode;
          reviewsNode.hidden = false;
          reviewsNode.removeAttribute("aria-hidden");
          reviewsNode.style.removeProperty("display");
          slot.append(reviewsNode);
          this.stopReviewsObserver();
          return;
        }
      }

      if (this.reviewsObserver || !document.body) return;
      this.reviewsObserver = new MutationObserver(() => this.mountReviews());
      this.reviewsObserver.observe(document.body, { childList: true, subtree: true });
      this.reviewsObserverTimer = window.setTimeout(() => this.stopReviewsObserver(), 20000);
    }

    start() {
      if (this.mode === "product") {
        document.addEventListener("change", this.onPotentialVariantChange, true);
        window.addEventListener("popstate", this.onPotentialVariantChange);
        for (const name of ["variant:change", "variant-change", "product:variant-change"]) {
          document.addEventListener(name, this.onCustomVariantChange);
        }
      }
      this.show(this.mode === "single" ? "single" : this.currentVariantId());
      if (this.mode === "product" && !this.hasCustomerReviews) this.mountReviews();
    }

    currentVariantId(explicit = null) {
      return parseVariantId(explicit) || variantFromUrl() || parseVariantId(this.root.dataset.initialVariantId);
    }

    onCustomVariantChange(event) {
      const id = event?.detail?.variant?.id || event?.detail?.variantId || event?.detail?.id;
      this.schedule(this.currentVariantId(id));
    }

    onPotentialVariantChange(event) {
      if (event?.target && !event.target.closest?.("form, variant-selects, variant-radios, product-form")) return;
      this.schedule(variantFromEventTarget(event?.target));
    }

    schedule(variantId = null) {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(() => this.show(this.currentVariantId(variantId)), 80);
    }

    shouldHideUnavailable() {
      return this.hideWithoutCharge && !this.designMode;
    }

    syncProductDescription(hide) {
      if (this.mode !== "product") return;
      const productId = String(this.root.dataset.productId || "");
      for (const description of document.querySelectorAll("[data-ls-product-description]")) {
        if (productId && description.dataset.productId !== productId) continue;
        description.hidden = Boolean(hide);
        if (hide) description.setAttribute("aria-hidden", "true");
        else description.removeAttribute("aria-hidden");
      }
    }

    restoreGuarantee() {
      if (!this.guaranteeNode || !this.guaranteeHome) return;
      this.guaranteeHome.querySelector("[data-ls-guarantee-slot]").append(this.guaranteeNode);
      this.guaranteeHome.hidden = false;
    }

    setStatus(message, loading = false) {
      this.restoreGuarantee();
      this.status.replaceChildren();
      if (loading) {
        const spinner = element("span", "ls-cp__spinner");
        spinner.setAttribute("aria-hidden", "true");
        this.status.append(spinner);
      }
      appendText(this.status, "span", "", message);
      this.status.hidden = false;
      this.content.hidden = true;
      if (!this.shouldHideUnavailable()) this.root.hidden = false;
    }

    renderUnavailable(message, retry = false, hide = false) {
      this.restoreGuarantee();
      this.syncProductDescription(false);
      if (hide) {
        this.status.hidden = true;
        this.content.hidden = true;
        this.content.replaceChildren();
        this.root.hidden = true;
        return;
      }
      this.root.hidden = false;
      this.status.hidden = true;
      this.content.hidden = false;
      this.content.replaceChildren();
      renderNotice(this.content, retry ? "Status wird aktualisiert" : "Noch kein öffentlicher Chargenpass", message, retry ? "warning" : "neutral");
      if (retry) {
        const button = element("button", "ls-cp__retry", "Erneut versuchen");
        button.type = "button";
        button.addEventListener("click", () => this.show(this.mode === "single" ? "single" : this.currentVariantId()), { once: true });
        this.content.append(button);
      }
    }

    render(data) {
      if (data.state === "updating") {
        this.renderUnavailable(data.message || "Der Chargenstatus wird gerade aktualisiert.", true, this.shouldHideUnavailable());
        return;
      }
      if (data.state !== "ready" && data.state !== "review") {
        this.renderUnavailable(
          data.message || "Für diese Auswahl ist derzeit kein freigegebener Chargenpass verfügbar.",
          false,
          this.shouldHideUnavailable(),
        );
        return;
      }
      const chargeDescription = data.charge?.description;
      const hasChargeDescription = Boolean(
        chargeDescription?.summary ||
        chargeDescription?.body ||
        (Array.isArray(chargeDescription?.highlights) && chargeDescription.highlights.length),
      );
      this.syncProductDescription(hasChargeDescription);
      this.root.hidden = false;
      this.status.hidden = true;
      this.content.hidden = false;
      this.content.replaceChildren();
      if (data.state === "review") {
        renderNotice(this.content, "Lokale Layoutvorschau", data.message || "Diese Vorschau ist nicht öffentlich und umgeht keine Freigaben.", "preview");
      }
      renderIdentity(this.content, data);
      if (this.mode === "single") renderProductImages(this.content, data.charge?.product_images);
      renderClassification(this.content, data.charge?.classification, data.state);
      renderDescription(this.content, data.charge?.description, data.state);
      renderSuitability(this.content, data.charge?.suitability);
      renderProcess(this.content, data.charge?.process);
      if (!data.charge?.classification) renderCharacteristics(this.content, data.charge?.characteristics, this.lexicon);
      renderAnalyses(this.content, data.charge?.analyses, this.lexicon);
      renderHygiene(this.content, data.charge?.hygiene_analyses, this.lexicon);
      renderMixture(this.content, data.charge?.mixture, data.charge?.area, this.lexicon);
      renderQualityPromise(this.content, data.charge?.quality_promise);
      if (this.hasCustomerReviews) {
        renderCustomerReviews(this.content, this.reviewsNode);
        this.mountReviews();
      }
      if (this.guaranteeNode) {
        this.content.append(this.guaranteeNode);
        this.guaranteeHome.hidden = true;
      }
      renderChronology(this.content, data.charge?.chronology);
    }

    show(variantId) {
      if (!variantId) {
        this.renderUnavailable(
          "Für diese Auswahl konnte keine Produktvariante bestimmt werden.",
          false,
          this.shouldHideUnavailable(),
        );
        return;
      }
      if (variantId === this.activeVariantId && !this.content.hidden) return;
      this.activeVariantId = variantId;
      const data = this.snapshots.get(variantId);
      if (!data) {
        this.renderUnavailable(
          "Für diese Auswahl ist derzeit kein freigegebener Chargenpass verfügbar.",
          false,
          this.shouldHideUnavailable(),
        );
        return;
      }
      this.render(data);
    }
  }

  function initialize(scope = document) {
    for (const root of scope.querySelectorAll("[data-ls-chargenpass]")) {
      if (root.dataset.lsChargenpassReady === "true") continue;
      root.dataset.lsChargenpassReady = "true";
      new ChargenpassController(root).start();
    }
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => initialize());
  else initialize();
  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
})();

